import type { Order } from './db'
import type { ShopSummary } from './api'

/**
 * Nói chuyện với sigoPOS Print Agent — chương trình nhỏ chạy nền ngay trên máy quầy.
 *
 * Nhờ nó mà app chạy trong trình duyệt thường vẫn in được bill, không phải đóng gói
 * thành app desktop chỉ để với tới máy in.
 */

const AGENT = 'http://127.0.0.1:9110'

export type PrinterKind = 'Windows' | 'Network' | 'Serial'

export interface PrinterConfig {
  kind: PrinterKind
  address: string
  port: number
  baudRate: number
  paperWidthMm: 58 | 80
  openDrawer: boolean
  cut: boolean
}

export const defaultPrinter: PrinterConfig = {
  kind: 'Network',
  address: '',
  port: 9100,
  baudRate: 9600,
  paperWidthMm: 58,
  openDrawer: false,
  cut: true,
}

export interface AgentInfo {
  name: string
  version: string
  machine: string
}

async function agentFetch(path: string, init?: RequestInit, timeoutMs = 12_000) {
  // Agent không chạy thì fetch treo cho tới khi hệ điều hành bỏ cuộc — người bán
  // đứng nhìn nút quay. Cắt sớm để còn kịp báo "chưa cài agent".
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), timeoutMs)
  try {
    return await fetch(AGENT + path, { ...init, signal: abort.signal })
  } finally {
    clearTimeout(timer)
  }
}

/** Có agent trên máy này không. Dùng để ẩn nút In bill khi chưa cài. */
export async function probeAgent(): Promise<AgentInfo | null> {
  try {
    const res = await agentFetch('/agent/ping', undefined, 2500)
    return res.ok ? ((await res.json()) as AgentInfo) : null
  } catch {
    return null
  }
}

export async function listPrinters(): Promise<{ windows: string[]; serial: string[] }> {
  const res = await agentFetch('/agent/printers', undefined, 5000)
  if (!res.ok) throw new Error('Không đọc được danh sách máy in')
  return (await res.json()) as { windows: string[]; serial: string[] }
}

function buildPayload(
  order: Order,
  shop: ShopSummary | null,
  printer: PrinterConfig,
  opts: { provisional?: boolean; cashierName?: string } = {},
) {
  return {
    target: {
      kind: printer.kind,
      address: printer.address,
      port: printer.port,
      baudRate: printer.baudRate,
      paperWidthMm: printer.paperWidthMm,
      // Bản tạm tính không cắt giấy và không mở két — khách còn ngồi, chưa trả tiền.
      openDrawer: opts.provisional ? false : printer.openDrawer,
      cut: printer.cut,
    },
    receipt: {
      shopName: shop?.name ?? 'Quán của tôi',
      address: shop?.address ?? null,
      phone: shop?.phone ?? null,
      taxCode: shop?.taxCode ?? null,
      code: order.code,
      printedAt: new Date().toISOString(),
      tableName: order.tableName,
      cashierName: opts.cashierName ?? null,
      lines: order.items.map((i) => ({
        name: i.name,
        option: i.optionLabel,
        qtyMilli: i.qtyMilli,
        unitPrice: i.unitPrice,
        lineTotal: i.lineTotal,
        note: i.note,
      })),
      subtotal: order.subtotal,
      discount: order.discount,
      total: order.total,
      paymentMethod: opts.provisional ? null : order.paymentMethod,
      amountGiven: order.amountGiven,
      changeGiven: order.changeGiven,
      footer: null,
      qrPayload: null,
      isProvisional: opts.provisional ?? false,
    },
  }
}

export async function printReceipt(
  order: Order,
  shop: ShopSummary | null,
  printer: PrinterConfig,
  opts: { provisional?: boolean; cashierName?: string } = {},
): Promise<void> {
  let res: Response
  try {
    res = await agentFetch('/agent/print', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload(order, shop, printer, opts)),
    })
  } catch {
    throw new Error('Chưa chạy chương trình in trên máy này. Mở sigoPOS Print Agent rồi thử lại.')
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null
    throw new Error(body?.message ?? 'Không in được.')
  }
}

/** Ảnh xem trước đúng y tờ giấy sẽ ra — để thử máy in mà không tốn giấy. */
export async function previewReceipt(
  order: Order,
  shop: ShopSummary | null,
  printer: PrinterConfig,
): Promise<string> {
  const res = await agentFetch('/agent/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildPayload(order, shop, printer)),
  })
  if (!res.ok) throw new Error('Không dựng được bản xem trước')
  return URL.createObjectURL(await res.blob())
}

export async function openDrawer(printer: PrinterConfig): Promise<void> {
  const res = await agentFetch('/agent/drawer', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind: printer.kind,
      address: printer.address,
      port: printer.port,
      baudRate: printer.baudRate,
      paperWidthMm: printer.paperWidthMm,
    }),
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null
    throw new Error(body?.message ?? 'Không mở được két.')
  }
}
